import { Fragment, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from '@mlm/ui';
import {
  applyCustomerCredit,
  createConsignmentInvoice,
  createConsignmentReceipt,
  createConsignmentReturn,
  getConsignment,
  getConsignmentCustomer,
  getConsignmentInvoice,
  getConsignmentReceipt,
  getCustomerSoa,
  listPosFundsAccounts,
  voidConsignment,
  voidConsignmentInvoice,
  voidConsignmentReceipt,
  voidConsignmentReturn,
  type ConsignmentCustomerDetail,
  type ConsignmentDocRow,
  type ConsignmentInvoiceSummary,
} from '../../lib/api';
import {
  openPrintShell,
  printBillingStatement,
  printConsignmentNote,
  printPaymentRecord,
  printSoa,
  type PrintShell,
} from '../../lib/consignment-print';
import { Plus, X } from 'lucide-react';
import { ExportButtons } from '../../components/export-buttons';
import {
  DOCS_CAP,
  DOC_LABEL,
  type ExportCtx,
  buildDocumentsExport,
  buildHoldingsExport,
  buildStatementExport,
} from '../../lib/consignment-export';
import { CONSIGNMENT_PERMS, defaultFundsAccountFor } from '@mlm/shared';
import {
  type BillingRow,
  addSplitRow,
  holdingKey,
  initialRows,
  overLimit,
  reconcileRows,
  remainingFor,
  removeRow,
  rowsFor,
  setRowPrice,
  setRowQty,
  toInvoiceLines,
} from '../../lib/consignment-billing-rows';
import { useHasPerm } from '../../lib/auth-store';
import { prettyError } from '../../lib/pos-errors';
import { useActiveBranch } from '../../lib/use-branch';
import { BranchPicker } from '../../components/branch-picker';

type BranchCtx = ReturnType<typeof useActiveBranch>;

/** Save-then-print flows still save when the popup is blocked; tell the cashier where the print went. */
const PRINT_BLOCKED_HINT =
  'Pop-ups are blocked, so nothing will print automatically — use Print on the Documents tab after saving.';

/** Body fragment naming the branch for an UNSCOPED caller (scoped = JWT wins). */
function branchBody(branch: BranchCtx): { branchId?: string } {
  return branch.isScoped ? {} : { branchId: branch.branchId ?? undefined };
}

const peso = (v: string | number) =>
  `₱${Number(v).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function apiError(e: unknown): string {
  if (isAxiosError(e)) {
    const code = (e.response?.data as { error?: string } | undefined)?.error ?? 'request_failed';
    return prettyError(code.split(':')[0]!);
  }
  return 'Request failed';
}

function displayName(c: ConsignmentCustomerDetail['customer']): string {
  return (
    c.fullName?.trim() || [c.firstName, c.lastName].filter(Boolean).join(' ').trim() || c.email
  );
}

const STATUS_BADGE: Record<string, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  posted: 'secondary',
  open: 'secondary',
  partial: 'outline',
  paid: 'default',
  void: 'destructive',
};

function monthRange(): { from: string; to: string } {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  const pad = (n: number) => String(n).padStart(2, '0');
  const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return { from: iso(from), to: iso(now) };
}

/**
 * Per-customer consignment workbench: holdings, documents, statement — plus
 * the three actions (record sales billing, receive payment, record return).
 */
export function ConsignmentCustomerPage() {
  const { userId = '' } = useParams();
  const qc = useQueryClient();
  // Returns restock and cash receipts count toward a branch — the till's
  // branch for a scoped cashier, a picked one for an admin.
  const branch = useActiveBranch();
  const detailQuery = useQuery({
    queryKey: ['consignment-customer', userId],
    queryFn: () => getConsignmentCustomer(userId),
    enabled: !!userId,
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['consignment-customer', userId] });
    void qc.invalidateQueries({ queryKey: ['consignment-customers'] });
    void qc.invalidateQueries({ queryKey: ['consignment-soa', userId] });
  };

  // Per-action perms (0162). Buttons are HIDDEN rather than disabled: on this
  // page `disabled` already means "nothing to act on" (no holdings / no open
  // billings), so reusing it for authority would be ambiguous.
  const canBill = useHasPerm(CONSIGNMENT_PERMS.bill);
  const canReceive = useHasPerm(CONSIGNMENT_PERMS.receivePayment);
  const canReturn = useHasPerm(CONSIGNMENT_PERMS.return);
  const canVoid = useHasPerm(CONSIGNMENT_PERMS.void);
  const canPrint = useHasPerm(CONSIGNMENT_PERMS.print);

  // Snapshotted at click time by the export buttons (so `generatedAt` is the
  // moment of export). Kept a plain function on purpose — memoizing it would
  // need a dep array and cost a lint warning for no benefit.
  const exportCtx = (): ExportCtx => ({
    consigneeName: detail ? displayName(detail.customer) : '',
    generatedAt: new Date(),
  });

  const [dialog, setDialog] = useState<null | 'billing' | 'payment' | 'return'>(null);
  const [voidTarget, setVoidTarget] = useState<ConsignmentDocRow | null>(null);

  const detail = detailQuery.data;
  const unitsOnHand = (detail?.holdings ?? []).reduce((s, h) => s + h.qty, 0);
  const openInvoices = (detail?.invoices ?? []).filter(
    (i) => i.status === 'open' || i.status === 'partial',
  );
  const receivable = openInvoices.reduce((s, i) => s + (Number(i.total) - Number(i.amountPaid)), 0);

  if (detailQuery.isLoading) {
    return <p className="py-10 text-center text-sm text-muted-foreground">Loading…</p>;
  }
  if (!detail) {
    return <p className="py-10 text-center text-sm text-muted-foreground">Consignee not found.</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">{displayName(detail.customer)}</h1>
          <p className="text-sm text-muted-foreground">
            {detail.customer.email}
            {detail.customer.phone ? ` · ${detail.customer.phone}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canBill && (
            <Button onClick={() => setDialog('billing')} disabled={unitsOnHand === 0}>
              Record Sales (Billing)
            </Button>
          )}
          {canReceive && (
            <Button
              variant="outline"
              onClick={() => setDialog('payment')}
              disabled={openInvoices.length === 0}
            >
              Receive Payment
            </Button>
          )}
          {canReturn && (
            <Button
              variant="outline"
              onClick={() => setDialog('return')}
              disabled={unitsOnHand === 0}
            >
              Record Return
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:max-w-md">
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Units on hand</p>
            <p className="mt-1 text-2xl font-semibold">{unitsOnHand}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Receivable</p>
            <p className="mt-1 text-2xl font-semibold">{peso(receivable)}</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="holdings">
        <TabsList>
          <TabsTrigger value="holdings">Holdings</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="statement">Statement</TabsTrigger>
        </TabsList>

        <TabsContent value="holdings">
          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0 pb-0">
              <CardTitle className="text-base">Holdings</CardTitle>
              <ExportButtons
                table={() => buildHoldingsExport(detail, exportCtx())}
                disabled={detail.holdings.length === 0}
              />
            </CardHeader>
            <CardContent className="pt-4">
              {detail.holdings.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  Nothing on consignment right now.
                </p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="py-2 pr-3">Product</th>
                      <th className="py-2 pr-3">Variation</th>
                      <th className="py-2 text-right">Qty on hand</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.holdings.map((h) => (
                      <tr
                        key={`${h.productId}:${h.variantId ?? ''}`}
                        className="border-b last:border-0"
                      >
                        <td className="py-2 pr-3 font-medium">{h.productName}</td>
                        <td className="py-2 pr-3 text-muted-foreground">{h.variantName ?? '—'}</td>
                        <td className="py-2 text-right">{h.qty}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documents">
          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0 pb-0">
              <CardTitle className="text-base">Documents</CardTitle>
              <ExportButtons
                table={() => buildDocumentsExport(detail, exportCtx())}
                disabled={detail.docs.length === 0}
                note={detail.docs.length >= DOCS_CAP ? `Latest ${DOCS_CAP} documents` : undefined}
              />
            </CardHeader>
            <CardContent className="pt-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="py-2 pr-3">Type</th>
                    <th className="py-2 pr-3">Doc No</th>
                    <th className="py-2 pr-3 text-right">Amount</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.docs.map((d) => (
                    <tr key={d.id + d.doc_type} className="border-b last:border-0">
                      <td className="py-2 pr-3">{DOC_LABEL[d.doc_type]}</td>
                      <td className="py-2 pr-3 font-mono">{d.doc_no}</td>
                      <td className="py-2 pr-3 text-right">{d.amount ? peso(d.amount) : '—'}</td>
                      <td className="py-2 pr-3">
                        <Badge variant={STATUS_BADGE[d.status] ?? 'secondary'}>{d.status}</Badge>
                      </td>
                      <td className="py-2 text-right">
                        <div className="flex justify-end gap-1">
                          {canPrint && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async () => {
                                if (d.doc_type === 'return') {
                                  toast.info('Returns have no printable document.');
                                  return;
                                }
                                // Open the window inside the click — after the
                                // fetch the browser blocks it as a popup.
                                const shell = openPrintShell((m) => toast.error(m));
                                if (!shell) return;
                                try {
                                  if (d.doc_type === 'consignment') {
                                    printConsignmentNote(await getConsignment(d.id), shell);
                                  } else if (d.doc_type === 'billing') {
                                    printBillingStatement(await getConsignmentInvoice(d.id), shell);
                                  } else {
                                    printPaymentRecord(await getConsignmentReceipt(d.id), shell);
                                  }
                                } catch {
                                  shell.close();
                                  toast.error('Could not load the document.');
                                }
                              }}
                            >
                              Print
                            </Button>
                          )}
                          {canVoid && d.status !== 'void' && (
                            <Button variant="ghost" size="sm" onClick={() => setVoidTarget(d)}>
                              Void
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="statement">
          <StatementTab
            userId={userId}
            customerName={displayName(detail.customer)}
            exportCtx={exportCtx}
          />
        </TabsContent>
      </Tabs>

      {dialog === 'billing' && (
        <BillingDialog
          detail={detail}
          canPrint={canPrint}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            refresh();
          }}
        />
      )}
      {dialog === 'payment' && (
        <ReceivePaymentDialog
          customerUserId={userId}
          invoices={openInvoices}
          credit={detail.credit ?? '0'}
          branch={branch}
          canPrint={canPrint}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            refresh();
          }}
        />
      )}
      {dialog === 'return' && (
        <ReturnDialog
          detail={detail}
          branch={branch}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            refresh();
          }}
        />
      )}
      {voidTarget && (
        <VoidDialog
          doc={voidTarget}
          onClose={() => setVoidTarget(null)}
          onDone={() => {
            setVoidTarget(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

// ── Statement tab ───────────────────────────────────────────────────────────

function StatementTab({
  userId,
  customerName,
  exportCtx,
}: {
  userId: string;
  customerName: string;
  exportCtx: () => ExportCtx;
}) {
  const [{ from, to }, setRange] = useState(monthRange());
  const soaQuery = useQuery({
    queryKey: ['consignment-soa', userId, from, to],
    queryFn: () =>
      getCustomerSoa(userId, {
        from: new Date(`${from}T00:00:00`).toISOString(),
        to: new Date(`${to}T23:59:59.999`).toISOString(),
      }),
    enabled: !!userId,
  });
  const soa = soaQuery.data;
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <CardTitle className="text-base">Statement of Account</CardTitle>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="block text-xs text-muted-foreground">From</label>
              <Input
                type="date"
                value={from}
                onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground">To</label>
              <Input
                type="date"
                value={to}
                onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
              />
            </div>
            <ExportButtons
              table={() => (soa ? buildStatementExport(soa, exportCtx()) : null)}
              onPrint={() => soa && printSoa(soa)}
              disabled={!soa || soaQuery.isFetching}
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">{customerName}</p>
      </CardHeader>
      <CardContent>
        {!soa ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-3">Date</th>
                  <th className="py-2 pr-3">Doc</th>
                  <th className="py-2 pr-3">No</th>
                  <th className="py-2 pr-3 text-right">Charges</th>
                  <th className="py-2 pr-3 text-right">Payments</th>
                  <th className="py-2 text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b bg-muted/30">
                  <td className="py-2 pr-3" colSpan={5}>
                    Opening balance
                  </td>
                  <td className="py-2 text-right font-medium">{peso(soa.openingBalance)}</td>
                </tr>
                {soa.lines.map((l, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="py-2 pr-3">{new Date(l.date).toLocaleDateString()}</td>
                    <td className="py-2 pr-3">{l.docType}</td>
                    <td className="py-2 pr-3 font-mono">{l.docNo}</td>
                    <td className="py-2 pr-3 text-right">
                      {Number(l.charges) ? peso(l.charges) : ''}
                    </td>
                    <td className="py-2 pr-3 text-right">
                      {Number(l.payments) ? peso(l.payments) : ''}
                    </td>
                    <td className="py-2 text-right">{peso(l.balance)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t">
                  <td className="py-2 pr-3 font-medium" colSpan={3}>
                    Totals
                  </td>
                  <td className="py-2 pr-3 text-right font-medium">{peso(soa.totalCharges)}</td>
                  <td className="py-2 pr-3 text-right font-medium">{peso(soa.totalPayments)}</td>
                  <td className="py-2 text-right text-base font-semibold">
                    {peso(soa.closingBalance)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Record Sales (Billing) dialog ───────────────────────────────────────────

function BillingDialog({
  detail,
  canPrint,
  onClose,
  onDone,
}: {
  detail: ConsignmentCustomerDetail;
  /** Without `consignment.print` the auto-print after save is skipped entirely
   *  — a null shell is NOT enough, `openPrintWindow` opens its own window. */
  canPrint: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const holdings = detail.holdings.filter((h) => h.qty > 0);
  const [rows, setRows] = useState<BillingRow[]>(() => initialRows(holdings));
  const [memo, setMemo] = useState('');

  // The dialog re-renders (never remounts) when the holdings query refetches,
  // so rows are reconciled against the latest holdings rather than seeded once
  // — otherwise a newly-appearing product would have no row to type into.
  const liveRows = reconcileRows(rows, holdings);
  if (liveRows !== rows) setRows(liveRows);

  const lines = toInvoiceLines(liveRows);
  const over = overLimit(liveRows, holdings);
  // Display-only arithmetic, as elsewhere on this page; the server recomputes
  // every figure from the submitted lines.
  const total = lines.reduce((s, l) => s + l.qty * Number(l.unitPrice || 0), 0);

  // The print window is opened in the click (before the save) — a window
  // opened after the request resolves is blocked as a popup.
  const submit = useMutation({
    mutationFn: (_shell: PrintShell | null) =>
      createConsignmentInvoice({
        customerUserId: detail.customer.id,
        lines,
        memo: memo.trim() || null,
      }),
    onSuccess: async (invoice, shell) => {
      toast.success(`Billing ${invoice.docNo} recorded`);
      onDone();
      if (!canPrint) return;
      try {
        printBillingStatement(await getConsignmentInvoice(invoice.id), shell);
      } catch {
        shell?.close(); // print is best-effort
      }
    },
    onError: (e, shell) => {
      shell?.close();
      toast.error(apiError(e));
    },
  });

  const valid =
    lines.length > 0 && lines.every((l) => Number(l.unitPrice) > 0) && over.length === 0;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Record Sales (Billing)</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Units the consignee has SOLD. This creates a billing (receivable) and reduces their
          consigned holdings — presyo na napagkasunduan ang ilagay. Use{' '}
          <Plus className="inline h-3 w-3" /> to bill the same product at a second price; the
          quantities together can't exceed what's on hand.
        </p>
        <div className="max-h-80 overflow-y-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2 pr-3">Product</th>
                <th className="py-2 pr-3 text-right">On hand</th>
                <th className="py-2 pr-3">Qty sold</th>
                <th className="py-2 pr-3">Unit price</th>
                <th className="py-2 w-16" />
              </tr>
            </thead>
            <tbody>
              {holdings.map((h) => {
                const key = holdingKey(h);
                const keyRows = rowsFor(liveRows, key);
                const exceeded = over.find((o) => o.key === key);
                return (
                  <Fragment key={key}>
                    {keyRows.map((r, i) => (
                      <tr
                        key={r.rowId}
                        className={
                          i === keyRows.length - 1 && !exceeded ? 'border-b last:border-0' : ''
                        }
                      >
                        <td className="py-2 pr-3">
                          {i === 0 ? (
                            <>
                              <span className="font-medium">{h.productName}</span>
                              {h.variantName ? (
                                <span className="text-muted-foreground"> · {h.variantName}</span>
                              ) : null}
                            </>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              ↳ same product, different price
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-right text-muted-foreground">
                          {i === 0 ? h.qty : ''}
                        </td>
                        <td className="py-2 pr-3">
                          <Input
                            type="number"
                            min={0}
                            // Already excludes this row's own qty — see
                            // remainingFor's docblock.
                            max={remainingFor(liveRows, key, h.qty, r.rowId)}
                            value={r.qty}
                            className="w-20"
                            onChange={(e) =>
                              setRows((prev) =>
                                setRowQty(prev, r.rowId, Number(e.target.value) || 0, h.qty),
                              )
                            }
                          />
                        </td>
                        <td className="py-2 pr-3">
                          <Input
                            inputMode="decimal"
                            placeholder="0.00"
                            value={r.unitPrice}
                            className="w-28"
                            onChange={(e) =>
                              setRows((prev) => setRowPrice(prev, r.rowId, e.target.value))
                            }
                          />
                        </td>
                        <td className="py-2">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Add another row at a different price"
                              aria-label={`Split ${h.productName}`}
                              onClick={() => setRows((prev) => addSplitRow(prev, r.rowId))}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                            {keyRows.length > 1 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                title="Remove this row"
                                aria-label={`Remove split row for ${h.productName}`}
                                onClick={() => setRows((prev) => removeRow(prev, r.rowId))}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {exceeded && (
                      <tr className="border-b last:border-0">
                        <td colSpan={5} className="pb-2 text-xs text-destructive">
                          Total quantity ({exceeded.total}) exceeds the {exceeded.onHand} on hand.
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <Input
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
          placeholder="Memo (optional)"
        />
        <DialogFooter className="items-center gap-3 sm:justify-between">
          <p className="text-sm">
            {over.length > 0 ? (
              <span className="text-destructive">Fix the highlighted quantities</span>
            ) : (
              <>
                Total: <span className="font-semibold">{peso(total)}</span>
              </>
            )}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              disabled={!valid || submit.isPending}
              onClick={() =>
                submit.mutate(
                  canPrint ? openPrintShell(() => toast.info(PRINT_BLOCKED_HINT)) : null,
                )
              }
            >
              {submit.isPending ? 'Saving…' : 'Record Billing'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Receive Payment dialog ──────────────────────────────────────────────────

const METHODS = ['cash', 'bank_transfer', 'gcash', 'check', 'other'] as const;

function ReceivePaymentDialog({
  customerUserId,
  invoices,
  credit,
  branch,
  canPrint,
  onClose,
  onDone,
}: {
  customerUserId: string;
  invoices: ConsignmentInvoiceSummary[];
  /** Money already paid that is not against any billing yet. */
  credit: string;
  branch: BranchCtx;
  /** See BillingDialog — no perm means no auto-print, not a null shell. */
  canPrint: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<(typeof METHODS)[number]>('cash');
  const [reference, setReference] = useState('');
  const [memo, setMemo] = useState('');
  const [alloc, setAlloc] = useState<Record<string, string>>({});
  // One idempotency key per dialog open — a double-tap can't double-post.
  const [clientRequestId] = useState(() => crypto.randomUUID());

  // Cash/Bank register. The list is already filtered server-side to accounts
  // this till may post to, and carries no balances.
  const fundsQ = useQuery({
    queryKey: ['pos-funds-accounts', branch.branchId],
    queryFn: () => listPosFundsAccounts(branch.branchId),
  });
  const fundsAccounts = fundsQ.data?.rows ?? [];
  const fundsRequired = fundsQ.data?.required ?? false;
  const [fundsTouched, setFundsTouched] = useState(false);
  const [fundsAccountId, setFundsAccountId] = useState('');
  const suggestedFunds =
    defaultFundsAccountFor(fundsAccounts, { method, branchId: branch.branchId }) ?? '';
  // A pick only survives while it is still on offer. Switching branch refetches
  // a different list, and an unscoped admin could otherwise post a collection
  // to the till they had selected for the PREVIOUS branch.
  const pickStillOffered = fundsAccounts.some((a) => a.id === fundsAccountId);
  const effectiveFundsAccountId =
    fundsTouched && pickStillOffered ? fundsAccountId : suggestedFunds;

  const sorted = useMemo(
    () =>
      [...invoices].sort(
        (a, b) => new Date(a.invoiceDate).getTime() - new Date(b.invoiceDate).getTime(),
      ),
    [invoices],
  );
  const balanceOf = (i: ConsignmentInvoiceSummary) => Number(i.total) - Number(i.amountPaid);

  /** Auto-apply the entered amount oldest-first (rows stay editable after). */
  function autoApply(raw: string) {
    setAmount(raw);
    let left = Number(raw) || 0;
    const next: Record<string, string> = {};
    for (const inv of sorted) {
      if (left <= 0) break;
      const take = Math.min(left, balanceOf(inv));
      if (take > 0) {
        next[inv.id] = take.toFixed(2);
        left = Number((left - take).toFixed(2));
      }
    }
    setAlloc(next);
  }

  const applied = Object.values(alloc).reduce((s, v) => s + (Number(v) || 0), 0);
  // Tender not applied to any billing. Since R2 Phase D this may be positive:
  // the remainder is kept as the customer's credit and can be applied later.
  // Only a NEGATIVE value is an error — that is applying money that did not
  // arrive.
  const unallocated = Number(((Number(amount) || 0) - applied).toFixed(2));
  // What the customer owes across the open billings, and what stays owed
  // once the applied lines post — the figures the cashier reads back.
  const totalOutstanding = sorted.reduce((s, i) => s + balanceOf(i), 0);
  const remainingAfter = Number((totalOutstanding - applied).toFixed(2));

  // The print window is opened in the click (before the save) — a window
  // opened after the request resolves is blocked as a popup.
  const submit = useMutation({
    mutationFn: (_shell: PrintShell | null) =>
      createConsignmentReceipt({
        customerUserId,
        ...branchBody(branch),
        amount: Number(amount).toFixed(2),
        method,
        reference: reference.trim() || null,
        allocations: Object.entries(alloc)
          .filter(([, v]) => Number(v) > 0)
          .map(([invoiceId, v]) => ({ invoiceId, amount: Number(v).toFixed(2) })),
        memo: memo.trim() || null,
        clientRequestId,
        fundsAccountId: effectiveFundsAccountId || null,
      }),
    onSuccess: async ({ receipt }, shell) => {
      toast.success(`Payment ${receipt.docNo} recorded`);
      onDone();
      if (!canPrint) return;
      try {
        printPaymentRecord(await getConsignmentReceipt(receipt.id), shell);
      } catch {
        shell?.close(); // print is best-effort
      }
    },
    onError: (e, shell) => {
      shell?.close();
      toast.error(apiError(e));
    },
  });

  const creditAvailable = Number(credit) || 0;
  /**
   * Spend credit the customer already left, rather than taking new money. It
   * settles the same allocation lines, so the cashier fills the table exactly
   * as for a payment and presses this instead of Post.
   */
  const spendCredit = useMutation({
    mutationFn: () =>
      applyCustomerCredit({
        customerUserId,
        allocations: Object.entries(alloc)
          .filter(([, v]) => Number(v) > 0)
          .map(([invoiceId, v]) => ({ invoiceId, amount: Number(v).toFixed(2) })),
      }),
    onSuccess: (r) => {
      toast.success(`Applied ${peso(Number(r.applied))} from credit.`);
      onDone();
    },
    onError: (e) => toast.error(apiError(e)),
  });

  // A line above its billing's balance is rejected server-side
  // (allocation_exceeds_outstanding) — say so before the click.
  const overAllocated = sorted.some((inv) => (Number(alloc[inv.id]) || 0) > balanceOf(inv) + 0.005);
  const valid =
    Number(amount) > 0 &&
    unallocated >= 0 &&
    !overAllocated &&
    (method !== 'cash' || !!branch.branchId) &&
    (!fundsRequired || !!effectiveFundsAccountId);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Receive Payment</DialogTitle>
        </DialogHeader>
        {!branch.isScoped && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-muted/30 px-3 py-2">
            <span className="text-xs text-muted-foreground">
              Cash is counted in this branch's drawer at shift close.
            </span>
            {branch.branches.length > 1 ? (
              <BranchPicker
                branches={branch.branches}
                branchId={branch.branchId}
                isScoped={branch.isScoped}
                onChange={(id) => branch.setSelectedBranchId(id)}
              />
            ) : (
              <span className="text-xs font-medium">
                {branch.branches.find((b) => b.id === branch.branchId)?.name ?? 'Loading branches…'}
              </span>
            )}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="col-span-2 sm:col-span-1">
            <label className="text-xs text-muted-foreground">Amount</label>
            <Input
              inputMode="decimal"
              value={amount}
              onChange={(e) => autoApply(e.target.value.replace(/[^0-9.]/g, ''))}
              placeholder="0.00"
              autoFocus
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Method</label>
            <select
              className="h-9 w-full rounded-md border bg-background px-2 text-sm"
              value={method}
              onChange={(e) => setMethod(e.target.value as (typeof METHODS)[number])}
            >
              {METHODS.map((m) => (
                <option key={m} value={m}>
                  {m.replace('_', ' ')}
                </option>
              ))}
            </select>
          </div>
          {(fundsAccounts.length > 0 || fundsRequired) && (
            <div>
              <label className="text-xs text-muted-foreground">
                Received to{fundsRequired ? '' : ' (optional)'}
              </label>
              <select
                className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                value={effectiveFundsAccountId}
                onChange={(e) => {
                  setFundsTouched(true);
                  setFundsAccountId(e.target.value);
                }}
              >
                <option value="">— none —</option>
                {fundsAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="text-xs text-muted-foreground">Reference</label>
            <Input value={reference} onChange={(e) => setReference(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Memo</label>
            <Input value={memo} onChange={(e) => setMemo(e.target.value)} />
          </div>
        </div>
        <div className="max-h-72 overflow-y-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2 pr-3">Billing</th>
                <th className="py-2 pr-3">Date</th>
                <th className="py-2 pr-3 text-right">Total</th>
                <th className="py-2 pr-3 text-right">Balance</th>
                <th className="py-2">Payment</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((inv) => (
                <tr key={inv.id} className="border-b last:border-0">
                  <td className="py-2 pr-3 font-mono">{inv.docNo}</td>
                  <td className="py-2 pr-3">{new Date(inv.invoiceDate).toLocaleDateString()}</td>
                  <td className="py-2 pr-3 text-right">{peso(inv.total)}</td>
                  <td className="py-2 pr-3 text-right">{peso(balanceOf(inv))}</td>
                  <td className="py-2">
                    <Input
                      inputMode="decimal"
                      value={alloc[inv.id] ?? ''}
                      className="w-28"
                      onChange={(e) =>
                        setAlloc((prev) => ({
                          ...prev,
                          [inv.id]: e.target.value.replace(/[^0-9.]/g, ''),
                        }))
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <DialogFooter className="items-center gap-3 sm:justify-between">
          <div className="text-sm text-muted-foreground">
            <p>
              Applied <span className="font-medium text-foreground">{peso(applied)}</span> of{' '}
              <span className="font-medium text-foreground">{peso(totalOutstanding)}</span>{' '}
              outstanding · left{' '}
              <span className="font-medium text-foreground">{peso(remainingAfter)}</span>
            </p>
            {unallocated > 0 && (
              <p className="text-xs text-muted-foreground">
                {peso(unallocated)} will be kept as this customer's credit.
              </p>
            )}
            {creditAvailable > 0 && (
              <p className="text-xs text-muted-foreground">
                This customer has {peso(creditAvailable)} in unapplied credit.
              </p>
            )}
            {unallocated < 0 && (
              <p className="text-xs text-destructive">
                Applied lines exceed the amount entered by {peso(-unallocated)}.
              </p>
            )}
            {overAllocated && (
              <p className="text-xs text-destructive">
                A payment line is larger than that billing's remaining balance.
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            {creditAvailable > 0 && (
              <Button
                variant="outline"
                disabled={
                  applied <= 0 ||
                  applied > creditAvailable + 0.005 ||
                  overAllocated ||
                  spendCredit.isPending
                }
                onClick={() => spendCredit.mutate()}
              >
                Apply available credit
              </Button>
            )}
            <Button
              disabled={!valid || submit.isPending}
              onClick={() =>
                submit.mutate(
                  canPrint ? openPrintShell(() => toast.info(PRINT_BLOCKED_HINT)) : null,
                )
              }
            >
              {submit.isPending ? 'Saving…' : 'Receive Payment'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Record Return dialog ────────────────────────────────────────────────────

function ReturnDialog({
  detail,
  branch,
  onClose,
  onDone,
}: {
  detail: ConsignmentCustomerDetail;
  branch: BranchCtx;
  onClose: () => void;
  onDone: () => void;
}) {
  const holdings = detail.holdings.filter((h) => h.qty > 0);
  const [qtys, setQtys] = useState<Record<string, number>>({});
  const [memo, setMemo] = useState('');

  const lines = Object.entries(qtys).filter(([, q]) => q > 0);

  const submit = useMutation({
    mutationFn: () =>
      createConsignmentReturn({
        customerUserId: detail.customer.id,
        ...branchBody(branch),
        lines: lines.map(([key, qty]) => {
          const [productId, variantId] = key.split('|');
          return { productId: productId!, variantId: variantId || null, qty };
        }),
        memo: memo.trim() || null,
      }),
    onSuccess: (r) => {
      toast.success(`Return ${r.docNo} recorded — stock is back in the branch`);
      onDone();
    },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Record Return</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Unsold units the consignee is handing back. Stock returns to the branch's inventory.
        </p>
        <BranchPicker
          branches={branch.branches}
          branchId={branch.branchId}
          isScoped={branch.isScoped}
          onChange={(id) => branch.setSelectedBranchId(id)}
        />
        <div className="max-h-72 overflow-y-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2 pr-3">Product</th>
                <th className="py-2 pr-3 text-right">On hand</th>
                <th className="py-2">Qty returned</th>
              </tr>
            </thead>
            <tbody>
              {holdings.map((h) => {
                const key = `${h.productId}|${h.variantId ?? ''}`;
                return (
                  <tr key={key} className="border-b last:border-0">
                    <td className="py-2 pr-3">
                      <span className="font-medium">{h.productName}</span>
                      {h.variantName ? (
                        <span className="text-muted-foreground"> · {h.variantName}</span>
                      ) : null}
                    </td>
                    <td className="py-2 pr-3 text-right text-muted-foreground">{h.qty}</td>
                    <td className="py-2">
                      <Input
                        type="number"
                        min={0}
                        max={h.qty}
                        value={qtys[key] ?? 0}
                        className="w-20"
                        onChange={(e) =>
                          setQtys((prev) => ({
                            ...prev,
                            [key]: Math.max(0, Math.min(h.qty, Number(e.target.value) || 0)),
                          }))
                        }
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Input
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
          placeholder="Memo (optional)"
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={lines.length === 0 || !branch.branchId || submit.isPending}
            onClick={() => submit.mutate()}
          >
            {submit.isPending ? 'Saving…' : 'Record Return'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Void dialog (shared by all four doc types) ──────────────────────────────

function VoidDialog({
  doc,
  onClose,
  onDone,
}: {
  doc: ConsignmentDocRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const submit = useMutation({
    mutationFn: () => {
      const r = reason.trim();
      if (doc.doc_type === 'consignment') return voidConsignment(doc.id, r);
      if (doc.doc_type === 'billing') return voidConsignmentInvoice(doc.id, r);
      if (doc.doc_type === 'payment') return voidConsignmentReceipt(doc.id, r);
      return voidConsignmentReturn(doc.id, r);
    },
    onSuccess: () => {
      toast.success(`${doc.doc_no} voided`);
      onDone();
    },
    onError: (e) => toast.error(apiError(e)),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Void {DOC_LABEL[doc.doc_type]} {doc.doc_no}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Voiding reverses this document's effects (stock and balances). It stays on record marked
          VOID. A reason is required.
        </p>
        <Input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason…"
          autoFocus
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={reason.trim().length < 3 || submit.isPending}
            onClick={() => submit.mutate()}
          >
            {submit.isPending ? 'Voiding…' : 'Void document'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
